import { CallEffect, SagaReturnType, takeEvery } from '@redux-saga/core/effects';
import {
    getPeriodLessons as apiGetPeriodLessons,
    getMonthLessons as apiGetMonthLessons,
} from '@yandex-int/k-common/client/api';
import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { GetLessonMetasResponse, getLessonMetas } from '@yandex-int/k-common/client/api/defs/get-lesson-metas';
import { showNetworkError } from 'platform-components/common';
import { all, call, put, takeLatest } from 'redux-saga/effects';

import { clessonResultsActions } from 'store/clesson-results/actions';

import { getClessonReactionsRequest } from '../course-view/actions';

import { actions } from './actions';
import { mapMonthCoursesLessons, mapPeriodLessons } from './mappers';
import type { CourseCalendarMarkedClesson } from './typings';
import { addDaysTimestamp, splitIntervalByCourses } from './utils';

export function* courseCalendarSagaWatcher() {
    yield all([
        takeEvery(actions.fetchDayLessons, fetchPeriodLessonsSaga),
        takeLatest(actions.fetchPeriodLessons, fetchPeriodLessonsSaga),
        takeLatest(actions.fetchMonthLessons, fetchMonthLessonsSaga),
    ]);
}

function* fetchPeriodLessonsSaga(action: ReturnType<typeof actions.fetchPeriodLessons>) {
    const { editorId, interval, courses, classId, subject, limit } = action.payload;

    try {
        const intervals = splitIntervalByCourses(interval, courses);
        const courseIds: number[] = courses.map(({ courseId }) => courseId);
        let data: any[] = yield all(
            intervals.reduce(
                (allRequests, { courseId, interval: dates }) => {
                    allRequests.push(
                        call(apiGetPeriodLessons, courseId, {
                            dates: dates.map(({ timestamp }) => timestamp),
                            limit,
                            timezone: dates[0].timezone,
                        })
                    );

                    return allRequests;
                },
                [] as CallEffect<SagaReturnType<typeof apiGetPeriodLessons>>[]
            )
        );

        const { cardsByDate, clessons, clessonList } = mapPeriodLessons(data, interval, courseIds);

        const metaSlugs = clessonList.map((clesson) => clesson.lesson.meta as unknown as string);
        let requestMetas = [...new Set(metaSlugs)];

        const res: GetLessonMetasResponse = yield call(getLessonMetas, requestMetas);
        clessonList.forEach((clesson) => {
            const slug = clesson.lesson.meta as unknown as string;
            clesson.lesson.meta = res[slug];
        });

        yield put(actions.updateClessonCards({ editorId, cardsByDate, clessons, isAllCardsLoaded: !limit }));
        yield put(actions.updateIntervalLoadingStatus({ editorId, status: LOADING_STATUSES.SUCCESS }));

        if (clessonList.length) {
            const clessonIds = clessonList.map(({ id }) => id);

            yield put(clessonResultsActions.getClessonsResultsRequest({ clessonIds }));
            yield put(getClessonReactionsRequest(clessonList, Number(classId), subject));
        }
    } catch (error) {
        console.error(error);
        yield put(actions.updateIntervalLoadingStatus({ editorId, status: LOADING_STATUSES.ERROR }));
        yield put(showNetworkError(error));
    }
}

function* fetchMonthLessonsSaga(action: ReturnType<typeof actions.fetchMonthLessons>) {
    const { editorId, interval, courses } = action.payload;

    try {
        yield put(actions.updateMarkedMonthDaysLoadingStatus({ editorId, status: LOADING_STATUSES.LOADING }));

        const intervals = splitIntervalByCourses(interval, courses);
        const data: CourseCalendarMarkedClesson[][] = yield all(
            intervals.map(({ courseId, interval: dates }) => {
                const start = dates[0];
                const end = dates[dates.length - 1];

                return call(apiGetMonthLessons, courseId, {
                    start_date: start.timestamp,
                    // https://st.yandex-team.ru/EDUCATION-24673
                    end_date: addDaysTimestamp(end.timestamp, 1),
                    timezone: start.timezone,
                });
            })
        );

        yield put(
            actions.fetchMonthLessonsSuccess({ editorId, markedMonthDays: mapMonthCoursesLessons(data, interval) })
        );
        yield put(actions.updateMarkedMonthDaysLoadingStatus({ editorId, status: LOADING_STATUSES.SUCCESS }));
    } catch (error) {
        console.error(error);
        yield put(actions.updateMarkedMonthDaysLoadingStatus({ editorId, status: LOADING_STATUSES.ERROR }));
        yield put(showNetworkError(error));
    }
}
